import React, { useEffect, useRef, useState } from "react";
import { SmilePlus } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export const REACTION_EMOJIS = ["👍", "❤️", "🎉", "😂", "😮", "🙌"];

/**
 * Multi-emoji reaction bar (LinkedIn/Slack-style).
 *
 * Props:
 *   - scope: "community" | "internal"
 *   - target: "post" | "comment"
 *   - id: post/comment id
 *   - reactions: { emoji: count } map
 *   - myReaction: emoji currently set by viewer (or null)
 *   - onChange: callback(nextReactions, nextMyReaction)
 *   - size: "sm" | "md"
 */
export default function EmojiReactions({
  scope = "community",
  target = "post",
  id,
  reactions = {},
  myReaction = null,
  onChange,
  size = "md",
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const react = async (emoji) => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    try {
      const path = target === "comment"
        ? `/${scope}/comments/${id}/react`
        : `/${scope}/posts/${id}/react`;
      const r = await api.post(path, { emoji });
      onChange?.(r.data.reactions || {}, r.data.my_reaction || null);
    } catch (e) {
      console.error("react failed", e);
      toast.error(e.response?.data?.detail || "Could not react");
    } finally {
      setBusy(false);
    }
  };

  const entries = REACTION_EMOJIS
    .map((e) => [e, reactions[e] || 0])
    .filter(([, c]) => c > 0);
  const total = entries.reduce((s, [, c]) => s + c, 0);

  const pad = size === "sm" ? "px-2 py-1 text-[12px]" : "px-2.5 py-1.5 text-sm";

  return (
    <div className="relative inline-flex items-center gap-1" ref={wrapRef}>
      {/* Existing reaction chips (clickable to toggle) */}
      {entries.map(([emoji, count]) => (
        <button
          key={emoji}
          type="button"
          onClick={() => react(emoji)}
          disabled={busy}
          className={`inline-flex items-center gap-1 rounded-full border ${pad} font-medium transition-colors disabled:opacity-50 ${
            myReaction === emoji
              ? "bg-fc-red/10 text-fc-red border-fc-red/40"
              : "bg-white text-fc-navy border-[#E5E7EB] hover:border-fc-red/50 hover:bg-fc-red/5"
          }`}
          data-testid={`reaction-chip-${id}-${emoji}`}
          aria-pressed={myReaction === emoji}
        >
          <span className="leading-none">{emoji}</span>
          <span className="font-bold tabular-nums">{count}</span>
        </button>
      ))}

      {/* + Add reaction trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-full ${pad} font-medium text-[#6B7280] hover:bg-gray-100 hover:text-fc-navy transition-colors`}
        data-testid={`reaction-add-${id}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <SmilePlus size={size === "sm" ? 13 : 15} />
        {total === 0 && <span className="hidden sm:inline">React</span>}
      </button>

      {/* Picker popover */}
      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 bg-white border border-[#E5E7EB] rounded-full shadow-lg px-2 py-1.5 flex items-center gap-1 z-30 animate-in fade-in slide-in-from-bottom-1"
          data-testid={`reaction-picker-${id}`}
        >
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => react(emoji)}
              disabled={busy}
              className={`text-[20px] leading-none w-8 h-8 rounded-full flex items-center justify-center hover:bg-fc-red/10 hover:scale-125 transition-transform disabled:opacity-50 ${
                myReaction === emoji ? "bg-fc-red/10" : ""
              }`}
              data-testid={`reaction-option-${id}-${emoji}`}
              aria-label={`React with ${emoji}`}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
